'use client';

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { cn } from '@fshr/shared-react';
import BevSafeText from '../bev-safe-text';
import Button from '../button';
import Link from '../link';
import TableSearch from './search/table-search';
import { filterRowsBySearch, formatResultsText } from './search/search-functions';
import { deriveColumnId, normalizeColumns, sortRowIndices } from './data/table-data-functions';
import SortableTableSection from './sortable-table-section/sortable-table-section';
import type { SortOrder, SortableTableProps, SortType } from './types';

const LAYOUT_DEBOUNCE_MS = 150;

export default function SortableTable({
	title,
	cta,
	columns,
	textSearch,
	numberOfViewMoreItems = 10,
	emptyCellText = 'Empty',
	emptyDataText = 'No data to display.',
	noResultsText = 'No results found',
	viewMoreText = 'View More',
	viewLessText = 'View Less',
	bleedWrapperClassName,
	scrollBlockClassName,
	tableContentClassName,
	bodyRowGroupClassName,
	stickyRowHeader = false,
	scrollTrackClassName,
}: SortableTableProps) {
	const tableId = useId();

	const contentClassName = tableContentClassName ?? bodyRowGroupClassName;

	const processedRows = useMemo(
		() => normalizeColumns(columns, emptyCellText),
		[columns, emptyCellText]
	);

	const columnMeta = useMemo(
		() =>
			columns.map((col, index) => ({
				...col,
				id: deriveColumnId(col, index),
				sortable: col.sortable !== false,
				sortType: col.sortType ?? 'text',
				hideOnMobile: col.hideOnMobile ?? false,
			})),
		[columns]
	);

	const [searchTerm, setSearchTerm] = useState('');
	const [sortOrders, setSortOrders] = useState<SortOrder[]>(() =>
		columns.map(() => -1 as SortOrder)
	);
	const [activeSortCol, setActiveSortCol] = useState<number | null>(null);
	const [activeSortDir, setActiveSortDir] = useState<'ascending' | 'descending' | null>(null);
	const [sortedIndices, setSortedIndices] = useState<number[]>(() =>
		processedRows.map((_, index) => index)
	);
	const [showingAll, setShowingAll] = useState(false);

	const filteredIndices = useMemo(
		() => filterRowsBySearch(processedRows, columns, searchTerm, textSearch),
		[processedRows, columns, searchTerm, textSearch]
	);

	const displayIndices = useMemo(
		() => sortedIndices.filter(index => filteredIndices.includes(index)),
		[sortedIndices, filteredIndices]
	);

	const visibleIndices = useMemo(() => {
		if (showingAll || displayIndices.length <= numberOfViewMoreItems) {
			return displayIndices;
		}
		return displayIndices.slice(0, numberOfViewMoreItems);
	}, [displayIndices, showingAll, numberOfViewMoreItems]);

	const showViewMore = displayIndices.length > numberOfViewMoreItems;
	const showResultsText = textSearch?.showResultsText !== false;
	const layoutKeyInput = `${visibleIndices.length}-${showingAll}-${columns.length}`;
	const [layoutKey, setLayoutKey] = useState(layoutKeyInput);
	const isEmptyData = processedRows.length === 0;
	const hasActiveSearch = Boolean(searchTerm);
	const showEmptyDataState = isEmptyData && !hasActiveSearch;

	useEffect(() => {
		setSortedIndices(processedRows.map((_, index) => index));
		setSortOrders(columns.map(() => -1 as SortOrder));
		setActiveSortCol(null);
		setActiveSortDir(null);
	}, [processedRows, columns]);

	useEffect(() => {
		setShowingAll(false);
	}, [searchTerm]);

	useEffect(() => {
		const delay = searchTerm ? LAYOUT_DEBOUNCE_MS : 0;
		const timer = setTimeout(() => setLayoutKey(layoutKeyInput), delay);
		return () => clearTimeout(timer);
	}, [layoutKeyInput, searchTerm]);

	const handleSort = useCallback(
		(colIndex: number, sortType: SortType) => {
			const order = sortOrders[colIndex];
			setSortedIndices(sortRowIndices(processedRows, colIndex, sortType, order));
			setActiveSortCol(colIndex);
			setActiveSortDir(order === -1 ? 'descending' : 'ascending');
			setSortOrders(prev =>
				prev.map((o, index) => (index === colIndex ? ((o * -1) as SortOrder) : (-1 as SortOrder)))
			);
		},
		[sortOrders, processedRows]
	);

	const resultsLabel = formatResultsText(displayIndices.length, visibleIndices.length);

	const tableSection = (
		<SortableTableSection
			tableId={tableId}
			layoutKey={layoutKey}
			columnMeta={columnMeta}
			processedRows={processedRows}
			visibleIndices={visibleIndices}
			stickyRowHeader={stickyRowHeader}
			scrollBlockClassName={scrollBlockClassName}
			contentClassName={contentClassName}
			scrollTrackClassName={scrollTrackClassName}
			noResultsText={noResultsText}
			showViewMore={showViewMore}
			showingAll={showingAll}
			onToggleShowingAll={() => setShowingAll(prev => !prev)}
			viewMoreText={viewMoreText}
			viewLessText={viewLessText}
			activeSortCol={activeSortCol}
			activeSortDir={activeSortDir}
			onSort={handleSort}
		/>
	);

	const tableContent = showEmptyDataState ? (
		<div
			role="status"
			className="flex justify-center items-center bg-surface-raised-subtle spacing-card-padding-y-m spacing-card-padding-x-l text-subtitle1 text-text-heading"
		>
			{emptyDataText}
		</div>
	) : bleedWrapperClassName ? (
		<div className={cn('min-w-0', bleedWrapperClassName)}>{tableSection}</div>
	) : (
		tableSection
	);

	return (
		<div className="flex w-full min-w-0 flex-col">
			{(title || cta) && (
				<div className="flex flex-col items-start spacing-stack-m spacing-heading-l md:flex-row md:items-center md:justify-between">
					{title ? (
						<h2 className="w-full text-h3 text-text-primary md:w-auto">
							<BevSafeText>{title}</BevSafeText>
						</h2>
					) : null}
					{cta ? (
						cta.link ? (
							<Link
								href={cta.link}
								intent="text"
								onClick={cta.onClick}
								className="w-fit self-start md:self-auto"
							>
								{cta.text}
							</Link>
						) : (
							<Button
								intent="text"
								size="small"
								onClick={cta.onClick}
								className="w-fit self-start md:self-auto"
							>
								{cta.text}
							</Button>
						)
					) : null}
				</div>
			)}

			<div className="flex w-full min-w-0 flex-col spacing-stack-xs">
				{textSearch ? (
					<TableSearch
						tableId={tableId}
						searchTerm={searchTerm}
						onSearchChange={setSearchTerm}
						placeholder={textSearch.text}
						resultsLabel={resultsLabel}
						showResultsText={showResultsText}
					/>
				) : null}

				{tableContent}
			</div>
		</div>
	);
}
